import {
  getCurrentPosition,
  getGeocodedAddressFromLatLng,
  getGeocodedAddressFromString,
  getLatLngFromAddressByGeocode,
  getLocationAutocompleteResults,
} from '@rover/react-lib/src/apis/geolocation.api';
import type {
  ActionCreator,
  Duck,
  DuckOptions,
  EffectCreator,
  SelectorCreator,
} from '@rover/react-lib/src/lib/redux-duck';
import { createDuck } from '@rover/react-lib/src/lib/redux-duck';
// eslint-disable-line import/no-unresolved
import type { Address, LatLng } from '@rover/types';

export type State = {
  locationAutocompleteResults: string[];
  resolvedAddress: Address | null | undefined;
  fetchGeocodedLatLngResponse: LatLng | null | undefined;
  errorMessage: string;
  loading: boolean;
};

type Actions = {
  fetchLocationAutocompleteResultsPending: ActionCreator<void>;
  fetchLocationAutocompleteResultsSuccess: ActionCreator<string[]>;
  fetchLocationAutocompleteResultsFailure: ActionCreator<{
    message: string;
  }>;
  resolveAddressPending: ActionCreator<void>;
  resolveAddressSuccess: ActionCreator<Address | null | undefined>;
  resolveAddressFailure: ActionCreator<{
    message: string;
  }>;
  fetchGeocodedLatLngPending: ActionCreator<void>;
  fetchGeocodedLatLngSuccess: ActionCreator<LatLng | null | undefined>;
  fetchGeocodedLatLngFailure: ActionCreator<{
    message: string;
  }>;
  clearAutocompleteResults: ActionCreator<void>;
};

type Effects = {
  fetchLocationAutocompleteResults: EffectCreator<string>;
  resolveAddress: EffectCreator<string | null | undefined>;
  fetchGeocodedLatLng: EffectCreator<{
    address?: string;
    region?: string;
  }>;
  clearAutocompleteResults: EffectCreator<void>;
};

type Selectors = {
  getLocationAutocompleteResults: SelectorCreator<string[]>;
  getResolvedAddress: SelectorCreator<Address | null | undefined>;
  getErrorMessage: SelectorCreator<string>;
  getIsSuccessful: SelectorCreator<boolean>;
  getGeocodedLatLng: SelectorCreator<LatLng | null | undefined>;
  getLoading: SelectorCreator<boolean>;
};

type Options = DuckOptions<State, Actions, Effects, Selectors>;

export const options: Options = {
  name: 'geolocation',
  initialState: {
    locationAutocompleteResults: [],
    resolvedAddress: null,
    fetchGeocodedLatLngResponse: null,
    errorMessage: '',
    loading: false,
  },
  actions: {
    fetchLocationAutocompleteResultsPending: () => (state) => ({ ...state, loading: true }),
    fetchLocationAutocompleteResultsSuccess: (locationAutocompleteResults) => (state) => ({
      ...state,
      loading: false,
      locationAutocompleteResults,
      errorMessage: '',
    }),
    fetchLocationAutocompleteResultsFailure:
      ({ message }) =>
      (state) => ({
        ...state,
        loading: false,
        errorMessage: message,
      }),
    resolveAddressPending: () => (state) => ({ ...state, loading: true }),
    resolveAddressSuccess: (resolvedAddress) => (state) => ({
      ...state,
      loading: false,
      resolvedAddress,
      errorMessage: '',
    }),
    resolveAddressFailure:
      ({ message }) =>
      (state) => ({
        ...state,
        loading: false,
        errorMessage: message,
      }),
    fetchGeocodedLatLngPending: () => (state) => ({ ...state, loading: true }),
    fetchGeocodedLatLngSuccess: (fetchGeocodedLatLngResponse) => (state) => ({
      ...state,
      loading: false,
      fetchGeocodedLatLngResponse,
      errorMessage: '',
    }),
    fetchGeocodedLatLngFailure:
      ({ message }) =>
      (state) => ({
        ...state,
        loading: false,
        errorMessage: message,
      }),
    clearAutocompleteResults: () => (state) => ({ ...state, locationAutocompleteResults: [] }),
  },
  effects: {
    fetchLocationAutocompleteResults:
      (query: string) => async (dispatch, getState, duckActions) => {
        dispatch(duckActions.fetchLocationAutocompleteResultsPending());

        try {
          dispatch(
            duckActions.fetchLocationAutocompleteResultsSuccess(
              await getLocationAutocompleteResults(query)
            )
          );
        } catch (error) {
          dispatch(duckActions.fetchLocationAutocompleteResultsFailure(error as Error));
        }
      },
    resolveAddress:
      (address: string | null | undefined) => async (dispatch, getState, duckActions) => {
        dispatch(duckActions.resolveAddressPending());

        try {
          let geocodedLatLng;

          if (address) {
            geocodedLatLng = await getGeocodedAddressFromString(address);
          } else {
            const position = await getCurrentPosition();
            geocodedLatLng = await getGeocodedAddressFromLatLng(position.coords);
          }

          dispatch(duckActions.resolveAddressSuccess(geocodedLatLng));
        } catch (error) {
          dispatch(duckActions.resolveAddressFailure(error as Error));
        }
      },
    fetchGeocodedLatLng:
      ({ address, region }) =>
      async (dispatch, getState, duckActions) => {
        dispatch(duckActions.fetchGeocodedLatLngPending());

        try {
          dispatch(
            duckActions.fetchGeocodedLatLngSuccess(
              (await getLatLngFromAddressByGeocode(address, region)) as LatLng
            )
          );
        } catch (error) {
          dispatch(duckActions.fetchGeocodedLatLngFailure(error as Error));
        }
      },
    clearAutocompleteResults: () => async (dispatch, getState, duckActions) => {
      dispatch(duckActions.clearAutocompleteResults());
    },
  },
  selectors: {
    getLocationAutocompleteResults: (getState, createSelector) =>
      createSelector([getState], (s) => s.locationAutocompleteResults),
    getResolvedAddress: (getState, createSelector) =>
      createSelector([getState], (s) => s.resolvedAddress),
    getGeocodedLatLng: (getState, createSelector) =>
      createSelector([getState], (s) => s.fetchGeocodedLatLngResponse),
    getErrorMessage: (getState, createSelector) =>
      createSelector([getState], (s) => s.errorMessage),
    getIsSuccessful: (getState, createSelector) =>
      createSelector([getState], (s) => !s.loading && !s.errorMessage),
    getLoading: (getState, createSelector) => createSelector([getState], (s) => s.loading),
  },
};

export const { actions, selectors, effects, reducer } = createDuck(options) as Duck<
  State,
  Actions,
  Effects,
  Selectors
>;
